import React, { useMemo } from 'react';
import { getActiveSalesPrice, gtinToJsonLd } from '../../../helpers/productHelper';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { originSelector } from '../../../modules/selectors/vHostRequestLocation';
import { useLocation } from 'react-router-dom';
import { JsonLd } from 'react-schemaorg';
import { seoMetaDataSelector } from '../../../modules/selectors/standort/seoMetaData';
import { Helmet } from 'react-helmet';
import { markenStandortSelector } from '../../../modules/selectors/standort/markenStandort';


function parseOpeningHours(openingHoursString) {
    if (!openingHoursString) return [];
    
    let normalizedString = openingHoursString
        .replace(/\s+/g, ' ')
        .replace(/\|/g, '|')
        .replace(/und/g, '|')
        .trim();
    
    if (/^\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}/.test(normalizedString)) {
        const [opens, closes] = normalizedString.split('-').map(time => time.trim());
        return [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
                "https://schema.org/Monday",
                "https://schema.org/Tuesday",
                "https://schema.org/Wednesday",
                "https://schema.org/Thursday",
                "https://schema.org/Friday",
                "https://schema.org/Saturday"
            ],
            opens,
            closes
        }];
    }
    
    const dayRanges = normalizedString.split('|').map(range => range.trim());
    
    return dayRanges.map(range => {
        let dayRange = '';
        let timeRange = '';
        
        const dayTimeMatch = range.match(/^([A-Za-zäöüß\s–-]+)(\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2})/);
        if (dayTimeMatch) {
            dayRange = dayTimeMatch[1].trim();
            timeRange = dayTimeMatch[2].trim();
        } else {
            const fullDayMatch = range.match(/^([A-Za-zäöüß\s]+)(?:bis|to|-)\s*([A-Za-zäöüß\s]+)(?:von|from)?\s*(\d{1,2}:\d{2})\s*(?:bis|to|-)\s*(\d{1,2}:\d{2})/);
            if (fullDayMatch) {
                dayRange = `${fullDayMatch[1].trim()}-${fullDayMatch[2].trim()}`;
                timeRange = `${fullDayMatch[3].trim()}-${fullDayMatch[4].trim()}`;
            } else {
                return null;
            }
        }
        
        const [opens, closes] = timeRange.split('-').map(time => time.trim());
        
        let dayOfWeek = [];
        
        const dayMap = {
            'Mo': 'Monday',
            'Di': 'Tuesday',
            'Mi': 'Wednesday',
            'Do': 'Thursday',
            'Fr': 'Friday',
            'Sa': 'Saturday',
            'So': 'Sunday',
            'Montag': 'Monday',
            'Dienstag': 'Tuesday',
            'Mittwoch': 'Wednesday',
            'Donnerstag': 'Thursday',
            'Freitag': 'Friday',
            'Samstag': 'Saturday',
            'Sonntag': 'Sunday'
        };
        
        // Handle weekday ranges
        if ((dayRange.includes('Mo') && dayRange.includes('Fr')) || 
            (dayRange.includes('Montag') && dayRange.includes('Freitag'))) {
            dayOfWeek = [
                "https://schema.org/Monday",
                "https://schema.org/Tuesday",
                "https://schema.org/Wednesday",
                "https://schema.org/Thursday",
                "https://schema.org/Friday"
            ];
        } else if (dayRange.includes('Mo') || dayRange.includes('Fr') || 
                  dayRange.includes('Montag') || dayRange.includes('Freitag')) {
            // Check for individual days
            for (const [abbr, full] of Object.entries(dayMap)) {
                if (dayRange.includes(abbr)) {
                    dayOfWeek.push(`https://schema.org/${full}`);
                }
            }
        } else if (dayRange.includes('Sa') || dayRange.includes('Samstag')) {
            dayOfWeek = ["https://schema.org/Saturday"];
        } else if (dayRange.includes('So') || dayRange.includes('Sonntag')) {
            dayOfWeek = ["https://schema.org/Sunday"];
        }
        
        if (dayOfWeek.length === 0) return null;
        
        return {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: dayOfWeek.length === 1 ? dayOfWeek[0] : dayOfWeek,
            opens,
            closes
        };
    }).filter(Boolean);
}

function getMetaData({ article, soldOut, origin, pathname, isExact, pickup, shipping, markenStandort }) {
    const {
        _id,
        bezeichnung,
        ean,
        googleDescription,
        gtin,
        hersteller: brand,
        id,
        images,
        isOwnArticle,
        marketingText: description,
        shownId,
        herstellerangaben,
    } = article;
    const { versandkosten: pickupVersandkosten } = pickup || {};
    const { versandkosten: shippingVersandkosten } = shipping || {};

    const { anschrift, oeffnungszeiten } = markenStandort;

    const { firma, strasse, ort, plz, } = anschrift;

    const hersteller = herstellerangaben ? herstellerangaben.firma : brand;

    const availability = `http://schema.org/${soldOut ? 'OutOfStock' : 'InStock'}`;
    const price = getActiveSalesPrice(article).toFixed(2);

    let sku;
    if (isOwnArticle) {
        sku = shownId || _id;
    } else {
        sku = id;
    }

    let name;
    if (isOwnArticle && googleDescription) {
        name = googleDescription;
    } else {
        name = bezeichnung;
    }

    const url = `${origin}${pathname}${isExact ? '' : '/exakt'}`;
    const gtinJsonLdData = gtinToJsonLd({ gtin: isOwnArticle ? ean : gtin });
    
    const openingHoursSpecification = parseOpeningHours(oeffnungszeiten);

    const baseMetaData = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        sku,
        images,
        name,
        description,
        brand: {
            '@type': 'Thing',
            name: hersteller,
        },
        offers: {
            '@type': 'Offer',
            url,
            itemCondition: 'http://schema.org/NewCondition',
            availability,
            price,
            priceCurrency: 'EUR',
        },
        ...gtinJsonLdData,
    };

    // Add shipping details if shipping data exists
    if (shipping) {
        if(shipping.legalType === 'online-kauf' && shippingVersandkosten) {
            baseMetaData.offers.shippingDetails = {
                '@type': 'OfferShippingDetails',
                shippingRate: {
                    '@type': 'MonetaryAmount',
                    value: shippingVersandkosten?.pauschale1 ?? 0.00,
                    currency: 'EUR',
                },
                shippingDestination: {
                    "@type": "DefinedRegion",
                    addressCountry: ["DE", "AT", "CH"]
                },
                deliveryTime: {
                    "@type": "ShippingDeliveryTime",
                    handlingTime: {
                        "@type": "QuantitativeValue",
                        minValue: 0,
                        maxValue: 1,
                        unitCode: "d",
                    },
                    transitTime: {
                        "@type": "QuantitativeValue",
                        minValue: article.deliveryDaysFrom ?? 1,
                        maxValue: article.deliveryDaysTo ?? 3,
                        unitCode: "d"
                    }
                }
            };
        } else {
            baseMetaData.hasPOS = {
                "@type": "Place",
                name: firma,
                address: {
                    "@type": "PostalAddress",
                    streetAddress: strasse,
                    addressLocality: ort,
                    postalCode: plz,
                    addressCountry: "DE"
                },
                openingHoursSpecification
            };
        }
        
    }

    // Add pickup details if pickup data exists
    if (pickup) {
        if(pickup.legalType === 'online-kauf' && pickupVersandkosten) {
            baseMetaData.offers.shippingDetails = {
                '@type': 'OfferShippingDetails',
                shippingRate: {
                    '@type': 'MonetaryAmount',
                    value: pickupVersandkosten?.pauschale1 ?? 0.00,
                    currency: 'EUR',
                },
                shippingDestination: {
                    "@type": "DefinedRegion",
                    addressCountry: ["DE", "AT", "CH"]
                },
                deliveryTime: {
                    "@type": "ShippingDeliveryTime",
                    handlingTime: {
                        "@type": "QuantitativeValue",
                        minValue: 0,
                        maxValue: 1,
                        unitCode: "d",
                    },
                    transitTime: {
                        "@type": "QuantitativeValue",
                        minValue: article.deliveryDaysFrom ?? 1,
                        maxValue: article.deliveryDaysTo ?? 3,
                        unitCode: "d"
                    }
                }
            };
        } else {
            baseMetaData.hasPOS = {
                "@type": "Place",
                name: firma,
                address: {
                    "@type": "PostalAddress",
                    streetAddress: strasse,
                    addressLocality: ort,
                    postalCode: plz,
                    addressCountry: "DE"
                },
                openingHoursSpecification
            };
        }
    }

    return baseMetaData;
}

function ProductDetailMeta({ variant, soldOut, isExact, pickup, shipping }) {
    const origin = useSelector(originSelector);
    const { pathname } = useLocation();
    const seoMetaData = useSelector(seoMetaDataSelector);
    const markenStandort = useSelector(markenStandortSelector);

    const metaData = useMemo(() => {
        return getMetaData({ article: variant, soldOut, origin, pathname, isExact, pickup, shipping, markenStandort });
    }, [isExact, origin, pathname, soldOut, pickup, shipping, markenStandort, variant]);

    const titlePrefix = seoMetaData?.title || variant.seoTitle || '';
    let titlePostfix = markenStandort?.seoMetaDataTitlePostfix ? ` | ${markenStandort?.seoMetaDataTitlePostfix}` : '| Autoteile plus Service';
    if (titlePostfix === " |  " || titlePrefix.includes(" | ")) {
        titlePostfix = "";
    }

    return (
        <>
            <Helmet>
                <title>{titlePrefix} {titlePostfix}</title>
            </Helmet>
            <JsonLd item={metaData} />
        </>
    );
}

export default ProductDetailMeta;
